import { z } from 'zod';
// The incident side of the red flag. Operations owns the manual per-item flag;
// `incident.ts` owns what a live incident contributes to it.
import { serviceRedFlagSchema } from './incident';

export const operationStatusCodeValues = [
  'S0',
  'S1',
  'S2',
  'S3',
  'S4',
  'S5',
  'S6',
  'S7',
  'S11',
  'S80',
  'S81',
  'S90',
  'S91',
] as const;

// Booking status (legacy itinerary_item.booking_status enum) — a manual
// supplier-booking lifecycle marker, only meaningful on Dining/Activity items.
export const bookingStatusValues = [
  'NotRequested',
  'Requested',
  'Rejected',
  'Confirmed',
  'Cancelled',
] as const;

export const bookingStatusSchema = z.enum(bookingStatusValues);
export type BookingStatus = z.infer<typeof bookingStatusSchema>;

// The Operations "Experience" grouping (modal, report/XLS sheet, row icon) spans
// BOTH Activity and Attraction content categories. This is NARROWER than the
// pricing `EXPERIENCE_CATEGORIES` (which also includes Dining) — in Operations,
// Dining is its own "Restaurants" section. Single source of truth so the modal
// filter, the report filter, and the row report-kind never drift apart (they
// did: an Attraction item silently vanished from the Experience modal/report).
export const EXPERIENCE_OPERATION_CATEGORIES = [
  'Activity',
  'Attraction',
] as const;

export const isExperienceOperationCategory = (
  category: string | null | undefined
): boolean => category === 'Activity' || category === 'Attraction';

// A pickup / drop-off resolved from the item it points at. Mirrors the fields
// `toLocation` (operations web `item-details.ts`) reads off a leg location, so
// the cell can consume either source without a second shape.
const operationItemLocationRefSchema = z.object({
  title: z.string().nullable(),
  formattedAddress: z.string().nullable(),
  city: z.string().nullable(),
  timezone: z.string().nullable(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
});
export type OperationItemLocationRef = z.infer<
  typeof operationItemLocationRefSchema
>;

// Sub-items ride alongside their parent in the API response. We declare the
// recursive shape with a base object + a lazy `children` field so consumers
// get strict typing for nested items via z.infer.
const operationItemBaseSchema = z.object({
  id: z.string().nullable(),
  title: z.unknown().nullable(),
  contentCategory: z.string().nullable(),
  contentSubcategory: z.string().nullable(),
  city: z.string().nullable(),
  country: z.string().nullable(),
  startTime: z.string().nullable(),
  endTime: z.string().nullable(),
  operationStatusId: z.string().nullable(),
  operationStatusTitle: z.string().nullable(),
  // Manual supplier-booking status (Dining/Activity only). Nullish so existing
  // fixtures/rows without the column still validate.
  bookingStatus: bookingStatusSchema.nullish(),
  tourOperators: z.unknown().nullable(),
  suppliers: z.unknown().nullable(),
  travellers: z.unknown().nullable(),
  attentionCategory: z.string().nullable(),
  attentionNote: z.string().nullable(),
  // Itinerary-version flag = MAX(item attention_category). When the itinerary
  // is red-flagged every one of its items shows the red Notification bell.
  itineraryAttentionCategory: z.string().nullable(),
  // The LIVE incidents that make this service read as red-flagged — its own,
  // plus every incident open for the whole itinerary. Derived per read (see
  // `enrichListRowRedFlags`), so resolving an incident un-flags its services
  // with nothing to un-write. Nullish: fixtures and the flagged/export queries
  // that don't enrich still validate.
  redFlagIncidents: z.array(serviceRedFlagSchema).nullish(),
  parentId: z.string().nullable(),
  parentTitle: z.unknown().nullable(),
  // Parent item's flight number — the fallback for a transfer sub-item.
  parentFlightNumber: z.string().nullish(),
  thumbnail: z.string().nullable(),
  transportType: z.string().nullable(),
  serviceType: z.string().nullable(),
  catalogTitle: z.string().nullable(),
  itineraryId: z.string().nullable(),
  itineraryTitle: z.string().nullable(),
  itineraryRef: z.string().nullable(),
  itineraryStatus: z.string().nullable(),
  itineraryPriorityLevel: z.string().nullable(),
  itineraryAccountingReference: z.string().nullable(),
  // The itinerary's country of travel — distinct from `country` above, which is
  // the service's own. Nullish: fixtures and older rows predate the view column.
  itineraryCountry: z.string().nullish(),
  itineraryContactFirstName: z.string().nullable(),
  itineraryContactLastName: z.string().nullable(),
  itineraryCompanyName: z.string().nullable(),
  // Itinerary owner (account) — surfaced on the Ops Dashboard drill-in list
  // (XT-380: item name, itinerary ref, location, owner, op_status).
  itineraryAccountFirstName: z.string().nullable().optional(),
  itineraryAccountLastName: z.string().nullable().optional(),
  operationDriverNotes: z.string().nullable(),
  customerNotes: z.string().nullable(),
  operationInternalNote: z.string().nullable(),
  languageCode: z.string().nullable(),
  // Track A — per-tab item parity (already on v_itinerary_item). JSONB
  // payloads are typed `unknown` (matching the other jsonb columns); read
  // them through the typed helpers below (`operationItemDetailsSchema`, …)
  // rather than asserting shapes inline.
  versionId: z.string().nullable(),
  operationSupplierReference: z.string().nullable(),
  details: z.unknown().nullable(),
  legs: z.unknown().nullable(),
  // Pickup / drop-off resolved from a SIBLING REFERENCE. The planner stores a
  // subitem's pickup as `details.pickup` = the id of another item on the same
  // trip ("collect them from that hotel"), and only sometimes copies the
  // address onto this item's own leg. Where it didn't, the leg carries just
  // city/country/coordinates and Operations had nothing to show. The service
  // resolves the reference per page (`enrichListRowLocationRefs`) so the row
  // can fall back to it; the item's OWN leg address still wins when present.
  // Nullish: fixtures, export and flagged queries that don't enrich still validate.
  referencedPickup: operationItemLocationRefSchema.nullish(),
  referencedDropoff: operationItemLocationRefSchema.nullish(),
  timezone: z.string().nullable(),
  attachments: z.unknown().nullable(),
  price: z.unknown().nullable(),
  currency: z.string().nullable(),
  itineraryTravellersContactInfo: z.unknown().nullable(),
  // i18n description array ([{ languageCode, text }]) from the itinerary item;
  // optional so existing fixtures/rows without it still validate. Read in the
  // edit-modal Preview tab.
  description: z.unknown().nullish(),
  // Itinerary-level private notes (HTML) — read-only in the edit-modal
  // "Itinerary Internal Notes" tab. Optional for fixture/back-compat.
  itineraryPrivateNotes: z.string().nullish(),
  // Public Item Notes + Private Item notes (item columns) and All Suppliers
  // notes (itinerary-level) — editable in the edit-modal note tabs. Optional
  // so existing fixtures/rows still validate.
  notes: z.string().nullish(),
  privateNotes: z.string().nullish(),
  allSuppliersNotes: z.string().nullish(),
  itineraryStartTime: z.string().nullable(),
  itineraryEndTime: z.string().nullable(),
  // Itinerary version day-count — used with `itineraryStartTime` to bound the
  // Operations Reschedule date picker to the trip interval (ES51972). Optional
  // for fixture/back-compat; a null just leaves the picker unbounded (the
  // backend snap remains the final guard).
  durationDays: z.number().nullish(),
  isPreviewHidden: z.boolean().nullable(),
});

// Typed view of the operation-item `details` JSONB (the legacy
// ItineraryItemDetails subset the operations row reads). Loose catchall keeps
// unknown keys; the typed-tab cells parse `item.details` through this.
export const operationItemDetailsSchema = z
  .object({
    venue: z.string().nullish(),
    priority: z.string().nullish(),
    languageCode: z.string().nullish(),
    meetingPoint: z.string().nullish(),
    leadTravellerName: z.string().nullish(),
    flightNumber: z.string().nullish(),
  })
  .catchall(z.unknown());

export type OperationItemDetails = z.infer<typeof operationItemDetailsSchema>;

export type OperationItem = z.infer<typeof operationItemBaseSchema> & {
  children?: OperationItem[];
};

export const operationItemSchema: z.ZodType<OperationItem> =
  operationItemBaseSchema.extend({
    children: z.lazy(() => z.array(operationItemSchema)).optional(),
  });

export const operationSortFields = ['product', 'date', 'status'] as const;

export const findOperationsInput = z.object({
  offset: z.number().int().nonnegative().default(0),
  limit: z.number().int().positive().max(100).default(20),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  search: z.string().optional(),
  searchField: z
    .enum(['optigest', 'traveler', 'customer', 'supplier'])
    .optional(),
  contentCategory: z.array(z.string()).optional(),
  contentSubcategory: z.array(z.string()).optional(),
  country: z.array(z.string()).optional(),
  operationStatuses: z.array(z.string()).optional(),
  itineraryPriorityLevel: z.array(z.string()).optional(),
  transportType: z.array(z.string()).optional(),
  serviceType: z.array(z.string()).optional(),
  // Transport "Not set" sentinel — matches items whose service_type IS NULL
  // (SQL `IN (...)` cannot match NULL). Composes with `serviceType` via OR.
  serviceTypeNotSet: z.boolean().optional(),
  languageCode: z.array(z.string()).optional(),
  itineraries: z.array(z.string()).optional(),
  venue: z.array(z.string()).optional(),
  // XT-538 Priority venues card. Carries no venue list of its own: the server
  // resolves it against `itineraries.venue.is_priority` in the caller's org, so
  // the drilled-in URL stays one boolean and the client never learns the set.
  // Replaced `venuePatterns` (XT-491), a hard-coded label list matched by
  // accent-folded LIKE. Patterns existed because `details.venue` was assumed to
  // be free text — but both writers (the planner's VenueSuggester and the ops
  // inline Select) are select-only, so a stored value IS a venue title and
  // exact equality is right. Pre-picker legacy rows with drifted spellings are
  // knowingly not counted.
  priorityVenuesOnly: z.boolean().optional(),
  label: z.array(z.string()).optional(),
  // Ticket "type" = the ticket subitem's `details.priority`
  // (regular | fast-track | private) — legacy parity with the v2 Operations
  // ProductCell that rendered the priority. Filters the Tickets tab.
  ticketType: z.array(z.string()).optional(),
  attentionCategory: z.string().optional(),
  itineraryAccountingReference: z.string().optional(),
  itineraryContactFirstName: z.string().optional(),
  itineraryTravellersContactInfo: z.string().optional(),
  suppliers: z.string().optional(),
  hasAttachments: z.enum(['any', 'with', 'without']).optional(),
  // XT-380 No PDF: item has no PDF attachment (url/type/mime looks like PDF).
  // Distinct from hasAttachments:'without' which means zero attachments of any kind.
  withoutPdf: z.boolean().optional(),
  // Driver/guide assignment filter (XT-225). Matches the ops-dashboard
  // materialised-view assigned/unassigned semantics over the `tour_operators`
  // JSONB array: unassigned = NULL or empty array; assigned = non-empty array.
  tourOperatorAssigned: z.enum(['assigned', 'unassigned']).optional(),
  skipDateRangeFilter: z.boolean().optional(),
  flaggedSort: z.string().optional(),
  flagged: z.enum(['flagged', 'preFlagged']).optional(),
  // Items with at least one failed WhatsApp send (msg_external_message
  // status='failed'). Powers the Ops Dashboard "Failed messages" card so its
  // count and drilled-in list share one engine (exact-count).
  failedMessages: z.boolean().optional(),
  // ─── XT-380 /operations per-tab metric filters ──────────────────────────
  // Filter by operation_status.status_code (org-scoped subquery), so cards can
  // target S0 Modified / S1 Non-Requested without resolving UUIDs client-side.
  operationStatusCodes: z.array(z.enum(operationStatusCodeValues)).optional(),
  // Exclude items whose status_code is any of these (e.g. No-PDF excludes
  // Non-Requested / Delivered / Cancelled).
  operationStatusCodesExclude: z
    .array(z.enum(operationStatusCodeValues))
    .optional(),
  // Traveller headcount ≥ N (array length, or details.people fallback).
  minTravellers: z.number().int().positive().optional(),
  // Item duration (end_time − start_time) strictly greater than N hours.
  minDurationHours: z.number().positive().optional(),
  // Language NOT IN list. Uses COALESCE(details.languageCode, language_code)
  // so guide subitems match the Details cell (XT-380 Languages card).
  languageCodeExclude: z.array(z.string()).optional(),
  // Items with at least one external message in any of these statuses.
  // `max_chars` ≈ content text length ≥ 1000 (WhatsApp field cap).
  messageStatuses: z
    .array(z.enum(['failed', 'expired', 'accepted', 'declined', 'max_chars']))
    .optional(),
  // Apply startDate/endDate to the itinerary's start (or ongoing overlap)
  // instead of the item start_time. Used by Home itinerary-level cards.
  itineraryDateMode: z.enum(['start', 'ongoing']).optional(),
  // Match itinerary_status text (case-insensitive). Used for Ready / Cancelled.
  itineraryStatuses: z.array(z.string()).optional(),
  // Itineraries that *transitioned* to one of these status names (case-
  // insensitive, `operation_log.entry_type = itinerary_status_change`,
  // `new_value.name`) within startDate/endDate. XT-380 Ready card — "moved to
  // Reservation Suppliers / Reservations OK in Chosen_daterange". When set,
  // startDate/endDate bound the log `created_on` (item start is skipped unless
  // also requested via other filters).
  itineraryStatusTransitionedTo: z.array(z.string()).optional(),
  /**
   * Itineraries whose `itinerary.cancelled_on` falls inside startDate/endDate.
   * XT-505 Cancelled card — "moved to Cancelled in the last 7 days".
   *
   * Replaces the operation-log transition match for that card: `cancelled_on`
   * is stamped by `saleDatePatchForStage` whenever an itinerary enters
   * `sales_stage = 20`, so it is org-independent (no status-NAME list to keep
   * in sync — an org calling it CANCELADO is still counted). When set,
   * startDate/endDate bound `cancelled_on` and the item/itinerary date window
   * is skipped. Only itineraries that were on an operational status
   * (`is_operational`) when cancelled are matched.
   *
   * Caveat (documented, deliberate): `cancelled_on` is write-once, so a
   * cancel → re-open → re-cancel keeps the FIRST date and the second
   * cancellation does not re-enter the window.
   */
  itineraryCancelledOnWithin: z.boolean().optional(),
  // Drop the sales-stage whitelist so Cancelled (stage −1) itineraries surface.
  includeNonOperational: z.boolean().optional(),
  // What `total` means on the list response (XT-380). Default `items` = root
  // item rows (legacy). `itineraries` = COUNT(DISTINCT itinerary_id) among
  // matches (Home starting/ongoing/ready/cancelled). `messages` = COUNT of
  // matching external messages (Messages expired/max/declined/accepted).
  // The row payload stays items so the drill-in list shows item name / ref /
  // location / owner / status as the ticket specifies.
  countMode: z.enum(['items', 'itineraries', 'messages']).optional(),
  // Also return `rowTotal` — the item-row total used for pagination. Only
  // meaningful with `countMode` ≠ items (where `total` counts itineraries /
  // messages while offset/limit still paginate root item rows). Costs one
  // extra COUNT query, so card count polls (limit:1) leave it off.
  includeRowTotal: z.boolean().optional(),
  /**
   * Count-only poll (filter cards). Runs the same filter engine for `total`
   * but skips root-row selection, sub-item fan-out, and row mapping — cards
   * only read `total`, so this keeps the exact-count invariant without the
   * list payload cost.
   */
  countOnly: z.boolean().optional(),
  /**
   * When paginating (offset > 0), the client may pass the total from the
   * previous page under the same filters so the server skips the expensive
   * COUNT. Not a security boundary — display-only. Ignored when offset is 0
   * (filters may have changed) or when countOnly is set. Capped so a corrupt
   * client cannot blow pagination UI with absurd numbers.
   */
  cachedTotal: z.number().int().nonnegative().max(10_000_000).optional(),
  /** Same as `cachedTotal` for countMode ≠ items pagination (`rowTotal`). */
  cachedRowTotal: z.number().int().nonnegative().max(10_000_000).optional(),
  // Scope to the caller's own itineraries (owner = the authenticated account).
  // The account id is injected server-side from ctx; never trusted from input.
  myItems: z.boolean().optional(),
  view: z.enum(['active', 'history']).default('active'),
  sortBy: z.enum(operationSortFields).optional(),
  sortOrder: z.enum(['asc', 'desc']).optional(),
});

export type FindOperationsInput = z.infer<typeof findOperationsInput>;

export const paginatedOperationsSchema = z.object({
  data: z.array(operationItemSchema),
  total: z.number(),
  // Item-row total for pagination (XT-380). Equals `total` unless countMode ≠
  // items + includeRowTotal requested the separate row count.
  rowTotal: z.number(),
  offset: z.number(),
  limit: z.number(),
});

export type PaginatedOperations = z.infer<typeof paginatedOperationsSchema>;

/**
 * Batch card-count poll (XT-380). One round-trip for every filter-card total
 * on a tab; each query uses the same filter engine as `list` + `countOnly`.
 * Cap keeps a misbehaving client from fan-out DoS.
 */
export const countOperationsManyInput = z.object({
  queries: z.array(findOperationsInput).min(1).max(20),
});

export type CountOperationsManyInput = z.infer<typeof countOperationsManyInput>;

export const countOperationsManyResult = z.object({
  /** Totals in the same order as `queries`. */
  totals: z.array(z.number().int().nonnegative()),
});

export type CountOperationsManyResult = z.infer<
  typeof countOperationsManyResult
>;

// ─── XLSX export ───────────────────────────────────────────────────

export const OPERATIONS_EXPORT_ROW_CAP = 10_000;

// Export uses the same filter shape as `list` but the pagination
// fields are advisory only (the export ignores `offset`/`limit` and
// streams up to OPERATIONS_EXPORT_ROW_CAP rows). Re-exporting the
// list schema directly keeps the two contracts in lockstep.
export const exportOperationsInput = findOperationsInput;
export type ExportOperationsInput = z.infer<typeof exportOperationsInput>;

// Result is a discriminated union: either the encoded workbook, or
// an `over-cap` payload so the UI can prompt the user to narrow.
export const exportOperationsResultSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('ok'),
    filename: z.string(),
    base64: z.string(),
    rows: z.number().int().nonnegative(),
  }),
  z.object({
    kind: z.literal('over-cap'),
    count: z.number().int().nonnegative(),
    cap: z.number().int().positive(),
  }),
]);
export type ExportOperationsResult = z.infer<
  typeof exportOperationsResultSchema
>;

export const operationStatusSchema = z.object({
  id: z.string(),
  title: z.string().nullable(),
  color: z.string().nullable(),
  statusCode: z.string().nullable(),
});

export type OperationStatus = z.infer<typeof operationStatusSchema>;

const filterCountEntry = z.object({
  value: z.string().nullable(),
  count: z.number(),
});

/**
 * Optional window for operations toolbar facet dropdowns (defaults to the
 * 7-day ops window). Named distinctly from CRM/tour-operator `filterCountsInput`.
 */
export const operationsFilterCountsInput = z
  .object({
    startDate: z.string().optional(),
    endDate: z.string().optional(),
  })
  .optional();

export type OperationsFilterCountsInput = z.infer<
  typeof operationsFilterCountsInput
>;

export const operationFilterCountsSchema = z.object({
  categories: z.array(filterCountEntry),
  countries: z.array(filterCountEntry),
  languages: z.array(filterCountEntry),
  venues: z.array(filterCountEntry),
  labels: z.array(filterCountEntry),
  // Distinct ticket types (`details.priority`) used by ticket items in the
  // window — sources the Tickets tab "Ticket type" filter.
  ticketTypes: z.array(filterCountEntry),
  serviceTypes: z.array(filterCountEntry),
  transportTypes: z.array(filterCountEntry),
  // Distinct supplier names used by the org's guide items in the date window —
  // sources the Guides "Provider" dropdown.
  guideProviders: z.array(filterCountEntry),
});

export type OperationFilterCounts = z.infer<typeof operationFilterCountsSchema>;

export const flaggedCountsSchema = z.object({
  flagged: z.number(),
  preFlagged: z.number(),
});

export type FlaggedCounts = z.infer<typeof flaggedCountsSchema>;

export const failedMessageCountSchema = z.object({
  count: z.number(),
});

export type FailedMessageCount = z.infer<typeof failedMessageCountSchema>;

export const filterItinerariesInput = z.object({
  search: z.string().min(3),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  flagged: z.boolean().optional(),
  offset: z.number().int().nonnegative().default(0),
  limit: z.number().int().positive().max(100).default(20),
});

export type FilterItinerariesInput = z.infer<typeof filterItinerariesInput>;

export const filterItineraryOption = z.object({
  id: z.string(),
  ref: z.string().nullable(),
  title: z.string().nullable(),
  country: z.string().nullable(),
  // The itinerary's own travel window — the toolbar seeds its date picker
  // from it when an itinerary is selected without a picked range (XT-437).
  // Optional: label-only producers (agent-panel hints, itinerary refs) don't
  // carry dates, and an undated itinerary has neither.
  startTime: z.string().nullable().optional(),
  endTime: z.string().nullable().optional(),
});

export type FilterItineraryOption = z.infer<typeof filterItineraryOption>;

export const filterItinerariesResult = z.object({
  data: z.array(filterItineraryOption),
  total: z.number(),
});

export type FilterItinerariesResult = z.infer<typeof filterItinerariesResult>;

// Resolve a set of itinerary IDs (from the URL) back to human-readable
// labels so the operations toolbar's `<ItinerariesFilter>` chip can render
// a title on a cold start (reload, shared link, back/forward) instead of
// falling back to the raw UUID. Bounded to match the chunk-safe MultiSelect.
export const itinerariesByIdsInput = z.object({
  ids: z.array(z.string()).min(1).max(100),
});

export type ItinerariesByIdsInput = z.infer<typeof itinerariesByIdsInput>;

export const itinerariesByIdsResult = z.array(filterItineraryOption);

export type ItinerariesByIdsResult = z.infer<typeof itinerariesByIdsResult>;

// ---------------------------------------------------------------------------
// Mutations — Phase 4: Status mutations + audit log
// ---------------------------------------------------------------------------

/**
 * itinerary_item.attention_category / itinerary_version.attention_category
 * are stored as numeric strings in the DB. Legacy `AttentionCategory` enum:
 * 0 = no attention, 10 = red flag. A red flag is set/cleared via the
 * Notification modal — a non-empty note flags the item (10), an empty note
 * clears it (0). `null` is treated as no attention.
 */
export const ATTENTION_NO_ATTENTION = '0';
export const ATTENTION_RED_FLAGGED = '10';
export const attentionCategoryValues = [
  ATTENTION_NO_ATTENTION,
  ATTENTION_RED_FLAGGED,
] as const;
export const attentionCategorySchema = z.enum(attentionCategoryValues);
export type AttentionCategory = z.infer<typeof attentionCategorySchema>;

/**
 * itinerary_version.priority_level is a numeric string in the DB. Legacy
 * parity: the levels are 5 / 10 / 15 / 20 / 40 / 60 / 80 (NOT-VERIFIED …
 * VIP+), NOT 1–5. Writing 1–5 would corrupt cross-reads with legacy.
 */
export const priorityLevelValues = [
  '5',
  '10',
  '15',
  '20',
  '40',
  '60',
  '80',
] as const;
export const priorityLevelSchema = z.enum(priorityLevelValues);
export type PriorityLevel = z.infer<typeof priorityLevelSchema>;

export const updateStatusInput = z.object({
  itineraryItemIds: z.array(z.uuid()).min(1),
  operationStatusId: z.uuid(),
});

export type UpdateStatusInput = z.infer<typeof updateStatusInput>;

export const updateStatusResultSchema = z.array(
  z.object({
    id: z.string(),
    operationStatusId: z.string().nullable(),
    operationStatusTitle: z.string().nullable(),
  })
);

export type UpdateStatusResult = z.infer<typeof updateStatusResultSchema>;

export const updateAttentionCategoryInput = z.object({
  itineraryItemId: z.uuid(),
  attentionCategory: attentionCategorySchema.nullable(),
  attentionNote: z.string().nullable(),
});

export type UpdateAttentionCategoryInput = z.infer<
  typeof updateAttentionCategoryInput
>;

export const updateBookingStatusInput = z.object({
  itineraryItemId: z.uuid(),
  bookingStatus: bookingStatusSchema,
});

export type UpdateBookingStatusInput = z.infer<typeof updateBookingStatusInput>;

export const updateBookingStatusResultSchema = z.object({
  id: z.uuid(),
  bookingStatus: bookingStatusSchema,
});

export const updateItemInput = z.object({
  itineraryItemId: z.uuid(),
  operationDriverNotes: z.string().nullable().optional(),
  customerNotes: z.string().nullable().optional(),
  operationInternalNote: z.string().nullable().optional(),
  // Public Item Notes + Private Item notes (item columns).
  notes: z.string().nullable().optional(),
  privateNotes: z.string().nullable().optional(),
  // All Suppliers notes — written to the itinerary, not the item.
  allSuppliersNotes: z.string().nullable().optional(),
  serviceType: z.string().nullable().optional(),
  transportType: z.string().nullable().optional(),
  // Reschedule (DateCell) — UTC wall-clock strings; written to the item
  // start/end columns. `legs` carries the matching leg time rewrite.
  startTime: z.string().nullable().optional(),
  endTime: z.string().nullable().optional(),
  details: z.record(z.string(), z.unknown()).optional(),
  // Track A2 — Details column writes. `legs` is replaced wholesale (the FE
  // edits a copy of the item's legs JSONB, e.g. start/end location);
  // `attachments` is replaced wholesale (ticket upload/list).
  legs: z.array(z.unknown()).optional(),
  attachments: z.array(z.unknown()).optional(),
});

export type UpdateItemInput = z.infer<typeof updateItemInput>;

export const updatePriorityLevelInput = z.object({
  itineraryVersionId: z.uuid(),
  priorityLevel: priorityLevelSchema.nullable(),
});

export type UpdatePriorityLevelInput = z.infer<typeof updatePriorityLevelInput>;

// OptiGest / accounting reference is a version-level field (the ProductCell
// inline-edit writes it). Mirrors updatePriorityLevel's version-scoped shape.
export const updateAccountingReferenceInput = z.object({
  itineraryVersionId: z.uuid(),
  // null clears the ref; empty string is rejected (the UI maps blank → null).
  accountingReference: z.string().min(1).nullable(),
});

export type UpdateAccountingReferenceInput = z.infer<
  typeof updateAccountingReferenceInput
>;

// The supplier reference is a free-text booking note on the item (booking
// code, guide's name and phone…). null clears it; the UI maps blank → null.
export const updateSupplierReferenceInput = z.object({
  itineraryItemId: z.uuid(),
  operationSupplierReference: z.string().trim().min(1).max(500).nullable(),
});

export type UpdateSupplierReferenceInput = z.infer<
  typeof updateSupplierReferenceInput
>;

// Travellers contact info is a version-level string[] of "Name:email:phone"
// entries (legacy shape). The ParticipantsCell editor replaces it wholesale.
export const updateTravellersContactInfoInput = z.object({
  itineraryVersionId: z.uuid(),
  travellersContactInfo: z.array(z.string()),
});

export type UpdateTravellersContactInfoInput = z.infer<
  typeof updateTravellersContactInfoInput
>;

// ---------------------------------------------------------------------------
// Phase 5 — Supplier + tour operator management
// ---------------------------------------------------------------------------

/**
 * `crmType` distinguishes COMPANY (1) from CONTACT (2). Legacy's numeric enum
 * persists it as a JSON number inside the supplier blob, while the rest of the
 * port (search results, the change dialog) uses the string form. Accept both
 * and normalise to the string so existing numeric rows round-trip through
 * `updateSuppliers` instead of failing validation.
 */
export const crmTypeValues = ['1', '2'] as const;
export const crmTypeSchema = z
  .union([z.literal('1'), z.literal('2'), z.literal(1), z.literal(2)])
  .transform((value) => (String(value) === '2' ? '2' : '1') as '1' | '2');
export type CrmType = z.infer<typeof crmTypeSchema>;

export const supplierDtoSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  crmType: crmTypeSchema,
  order: z.number().int().nonnegative(),
  selected: z.boolean(),
  primary: z.boolean(),
  crmInfo: z.unknown().optional(),
});

export type SupplierDto = z.infer<typeof supplierDtoSchema>;

// Itinerary-wide report (the operations "Information" modals): every item of an
// itinerary (all days, incl. sub-items) with its suppliers resolved to
// name/email/phone via crmInfo.
export const itineraryReportInput = z.object({
  itineraryId: z.uuid(),
});

export type ItineraryReportInput = z.infer<typeof itineraryReportInput>;

export const operationReportItemSchema = z.object({
  id: z.string(),
  title: z.unknown().nullable(),
  catalogTitle: z.string().nullable(),
  // Live `products.product.title`, joined per read — the sibling pickup /
  // drop-off label prefers it over the item's `title` snapshot so a renamed
  // property stops haunting other services' PU / DO (XT-621).
  productTitle: z.unknown().nullable().optional(),
  contentCategory: z.string().nullable(),
  contentSubcategory: z.string().nullable(),
  transportType: z.string().nullable(),
  serviceType: z.string().nullable(),
  city: z.string().nullable(),
  country: z.string().nullable(),
  startTime: z.string().nullable(),
  endTime: z.string().nullable(),
  parentId: z.string().nullable(),
  legs: z.unknown().nullable(),
  details: z.unknown().nullable(),
  travellers: z.unknown().nullable(),
  languageCode: z.string().nullable(),
  itineraryTravellersContactInfo: z.unknown().nullable(),
  operationDriverNotes: z.string().nullable(),
  operationSupplierReference: z.string().nullable(),
  price: z.unknown().nullable(),
  tourOperators: z.unknown().nullable(),
  itineraryRef: z.string().nullable(),
  itineraryTitle: z.unknown().nullable(),
  itineraryAccountingReference: z.string().nullable(),
  // Itinerary-level "All supplier Notes" (legacy `all_suppliers_notes`).
  // Primary source for the report "Restrictions" field.
  allSuppliersNotes: z.string().nullable(),
  resolvedSuppliers: z.array(supplierDtoSchema),
});

export type OperationReportItem = z.infer<typeof operationReportItemSchema>;

export const operationReportServiceType = z.enum([
  'transport',
  'experience',
  'accommodation',
  'dining',
  'guide',
]);

export type OperationReportServiceType = z.infer<
  typeof operationReportServiceType
>;

export const exportReportXlsxInput = z.object({
  itineraryId: z.uuid(),
  serviceType: operationReportServiceType,
});

export type ExportReportXlsxInput = z.infer<typeof exportReportXlsxInput>;

export const exportReportXlsxResult = z.object({
  filename: z.string(),
  base64: z.string(),
  mime: z.string(),
});

export type ExportReportXlsxResult = z.infer<typeof exportReportXlsxResult>;

export const itemSuppliersInput = z.object({
  itineraryItemId: z.uuid(),
});

export type ItemSuppliersInput = z.infer<typeof itemSuppliersInput>;

// "One Supplier note" tab — edits the CRM company/contact `notes` field for a
// single supplier (the note lives on the CRM record, not the itinerary item).
export const updateSupplierNoteInput = z.object({
  supplierId: z.uuid(),
  crmType: crmTypeSchema,
  note: z.string(),
});

export type UpdateSupplierNoteInput = z.infer<typeof updateSupplierNoteInput>;

export const updateSuppliersInput = z.object({
  itineraryItemIds: z.array(z.uuid()).min(1),
  suppliers: z.array(supplierDtoSchema),
});

export type UpdateSuppliersInput = z.infer<typeof updateSuppliersInput>;

export const updateSuppliersResultSchema = z.array(
  z.object({
    id: z.string(),
    suppliers: z.array(supplierDtoSchema),
  })
);

export type UpdateSuppliersResult = z.infer<typeof updateSuppliersResultSchema>;

export const tourOperatorAssignmentViewValues = ['future', 'past'] as const;
export const tourOperatorAssignmentViewSchema = z.enum(
  tourOperatorAssignmentViewValues
);
export type TourOperatorAssignmentView = z.infer<
  typeof tourOperatorAssignmentViewSchema
>;

/**
 * Mirrors the relevant `crm.tour_operator` columns plus a `conflictItems`
 * array surfacing IDs whose time slots overlap for this operator.
 */
export const tourOperatorDtoSchema = z.object({
  id: z.string(),
  fullName: z.string(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  type: z.string(),
  country: z.string().nullable(),
  city: z.string().nullable(),
  rating: z.string().nullable(),
  frequency: z.string().nullable(),
  languages: z.array(z.string()).nullable(),
  receiveMessage: z.string(),
  conflictItems: z.array(z.string()),
});

export type TourOperatorDto = z.infer<typeof tourOperatorDtoSchema>;

export const tourOperatorAssignmentItemSchema = z.object({
  id: z.string(),
  title: z.unknown().nullable(),
  startTime: z.string().nullable(),
  endTime: z.string().nullable(),
  itineraryRef: z.string().nullable(),
  country: z.string().nullable(),
  city: z.string().nullable(),
  contentCategory: z.string().nullable(),
  operationStatusTitle: z.string().nullable(),
});

export type TourOperatorAssignmentItem = z.infer<
  typeof tourOperatorAssignmentItemSchema
>;

export const findTourOperatorAssignmentsInput = z.object({
  // The operations-page list filters, so the assignments stay scoped to the
  // same items the list shows (and the date window keeps the scan bounded).
  filters: findOperationsInput.optional(),
  // Operator-location filters (legacy v2 parity — the panel's own Country/City
  // dropdowns narrow the *operator* list by the operator's own address, not by
  // the activity location). Values come from `tourOperatorLocations`; matching
  // is case-insensitive substring (legacy LIKE) against the operator's
  // `country` / `city` CRM columns.
  country: z.array(z.string()).optional(),
  city: z.array(z.string()).optional(),
  offset: z.number().int().nonnegative().default(0),
  limit: z.number().int().positive().max(100).default(20),
});

export type FindTourOperatorAssignmentsInput = z.infer<
  typeof findTourOperatorAssignmentsInput
>;

export const tourOperatorLocationsSchema = z.object({
  countries: z.array(z.string()),
  cities: z.array(z.string()),
});

export type TourOperatorLocations = z.infer<typeof tourOperatorLocationsSchema>;

export const tourOperatorAssignmentGroupSchema = z.object({
  tourOperator: tourOperatorDtoSchema,
  items: z.array(tourOperatorAssignmentItemSchema),
});

export type TourOperatorAssignmentGroup = z.infer<
  typeof tourOperatorAssignmentGroupSchema
>;

export const paginatedTourOperatorAssignmentsSchema = z.object({
  data: z.array(tourOperatorAssignmentGroupSchema),
  total: z.number(),
  countAll: z.number(),
  offset: z.number(),
  limit: z.number(),
});

export type PaginatedTourOperatorAssignments = z.infer<
  typeof paginatedTourOperatorAssignmentsSchema
>;

// ---------------------------------------------------------------------------
// Phase 5 (continued) — Tour-operator CRUD + supplier search
// ---------------------------------------------------------------------------

export const tourOperatorTypeValues = ['Driver', 'Guide'] as const;
export const tourOperatorTypeSchema = z.enum(tourOperatorTypeValues);
export type TourOperatorType = z.infer<typeof tourOperatorTypeSchema>;

export const tourOperatorFrequencyValues = [
  'Frequently',
  'Occasionally',
  'Rare',
] as const;
export const tourOperatorFrequencySchema = z.enum(tourOperatorFrequencyValues);
export type TourOperatorFrequency = z.infer<typeof tourOperatorFrequencySchema>;

export const tourOperatorReceiveMessageValues = [
  'allowed',
  'only_preparation',
  'not_allowed',
] as const;
export const tourOperatorReceiveMessageSchema = z.enum(
  tourOperatorReceiveMessageValues
);
export type TourOperatorReceiveMessage = z.infer<
  typeof tourOperatorReceiveMessageSchema
>;

/**
 * Full row representation used by tour-operator CRUD endpoints. Mirrors
 * `crm.tour_operator` columns; `rating` is exposed as a string to preserve
 * Postgres `numeric` precision through tRPC.
 */
export const tourOperatorRowSchema = z.object({
  id: z.uuid(),
  fullName: z.string(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  companyId: z.uuid(),
  city: z.string().nullable(),
  country: z.string().nullable(),
  type: tourOperatorTypeSchema,
  rating: z.string().nullable(),
  frequency: tourOperatorFrequencySchema.nullable(),
  receiveMessage: tourOperatorReceiveMessageSchema,
  languages: z.array(z.string()).nullable(),
  specials: z.array(z.string()).nullable(),
  notes: z.string().nullable(),
  createdOn: z.string(),
  modifiedOn: z.string().nullable(),
  // False when the operator is already booked in the requested time window
  // (set only when `startTime`/`endTime` are passed to the list query).
  isAvailable: z.boolean().optional(),
});

export type TourOperatorRow = z.infer<typeof tourOperatorRowSchema>;

export const findTourOperatorsInput = z.object({
  search: z.string().optional(),
  type: z.array(tourOperatorTypeSchema).optional(),
  // Item time window (UTC wall-clock). When both are present, each row gets an
  // `isAvailable` flag = no overlapping assignment exists for that operator.
  startTime: z.string().optional(),
  endTime: z.string().optional(),
  offset: z.number().int().nonnegative().default(0),
  limit: z.number().int().positive().max(100).default(20),
});

export type FindTourOperatorsInput = z.infer<typeof findTourOperatorsInput>;

export const paginatedTourOperatorsSchema = z.object({
  data: z.array(tourOperatorRowSchema),
  total: z.number(),
  offset: z.number(),
  limit: z.number(),
});

export type PaginatedTourOperators = z.infer<
  typeof paginatedTourOperatorsSchema
>;

export const createTourOperatorInput = z.object({
  fullName: z.string().min(1),
  type: tourOperatorTypeSchema,
  receiveMessage: tourOperatorReceiveMessageSchema,
  companyId: z.uuid(),
  phone: z.string().optional(),
  email: z.string().optional(),
  city: z.string().optional(),
  country: z.string().optional(),
  rating: z.number().int().min(1).max(5).optional(),
  frequency: tourOperatorFrequencySchema.optional(),
  languages: z.array(z.string()).optional(),
  specials: z.array(z.string()).optional(),
  notes: z.string().optional(),
});

export type CreateTourOperatorInput = z.infer<typeof createTourOperatorInput>;

export const updateTourOperatorInput = createTourOperatorInput
  .partial()
  .extend({
    id: z.uuid(),
  });

export type UpdateTourOperatorInput = z.infer<typeof updateTourOperatorInput>;

export const removeTourOperatorInput = z.object({ id: z.uuid() });

export type RemoveTourOperatorInput = z.infer<typeof removeTourOperatorInput>;

export const tourOperatorByIdInput = z.object({ id: z.uuid() });

export type TourOperatorByIdInput = z.infer<typeof tourOperatorByIdInput>;

/**
 * Lightweight match returned by the supplier picker. The dialog only needs
 * `{ id, name }` to append a new entry to the supplier list.
 */
export const supplierSearchResultSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  crmType: crmTypeSchema,
});

export type SupplierSearchResult = z.infer<typeof supplierSearchResultSchema>;

export const searchSuppliersInput = z.object({
  crmType: crmTypeSchema,
  name: z.string().min(1),
});

export type SearchSuppliersInput = z.infer<typeof searchSuppliersInput>;
